import { StackScreenProps } from "@react-navigation/stack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "native-base";
import React, { useState } from "react";

import Toast from "@/components/atoms/Toast";
import { useViewport } from "@/contexts/ViewportContext";
import { useAppNavigation } from "@/hooks/application/navigation";
import { ORDER_BY_ID_QUERY_KEY, useOrderById } from "@/hooks/domain/order";
import { OrderRate } from "@/interfaces/domain/Order";
import { RootNavigation } from "@/navigation/Root";
import { OrderStackParamList } from "@/navigation/stacks/OrderStack";
import { evaluateOrder } from "@/services/api/order";
import { logger } from "@/services/logger";

import OrderEvaluationMobile from "./OrderEvaluation.mobile";
import OrderEvaluationWeb from "./OrderEvaluation.web";
import { AppEvaluationData, DeliveryEvaluationData, StoreEvaluationData } from "./types";

type ScreenProps = StackScreenProps<OrderStackParamList, "orderEvaluation">;

const OrderEvaluation = ({ route }: ScreenProps) => {
  const orderId = route?.params?.orderId;

  const { isWebMode } = useViewport();
  const { goBack } = useAppNavigation<RootNavigation>();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState(0);
  const [storeEvaluation, setStoreEvalution] = useState<StoreEvaluationData>();
  const [deliveryEvaluation, setDeliveryEvaluation] = useState<DeliveryEvaluationData>();

  const { data: order, isLoading } = useOrderById(orderId, {
    enabled: !!orderId,
    onError: () => {
      if (!orderId) {
        goBack();
      }
    },
  });

  const { mutate, isLoading: isSubmiting } = useMutation<void, unknown, OrderRate>({
    mutationFn: (payload) => evaluateOrder(orderId, payload),
    onSuccess: () => {
      toast.show({
        render: () => <Toast variant="success" message="Compra avaliada com sucesso!" />,
      });
      queryClient.refetchQueries([ORDER_BY_ID_QUERY_KEY(orderId)]);
      goBack();
    },
    onError: (error) => {
      logger.error(error);
      toast.show({
        render: () => (
          <Toast
            variant="error"
            message="Erro ao avaliar sua compra, tente novamente mais tarde!"
          />
        ),
      });
      goBack();
    },
  });

  const onCancel = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const onSubmitAppEvaluation = (data: AppEvaluationData) => {
    mutate({
      avaliacao_app: {
        nota: data.rate,
      },
      avaliacao_entrega: {
        chegou: deliveryEvaluation.delivered,
        nota: deliveryEvaluation.rate,
      },
      avalicao_servico: {
        comentario: storeEvaluation.comment,
        nota: storeEvaluation.rate,
        servicos: storeEvaluation.services,
      },
    });
  };

  const onSubmitDeliveryEvaluation = (data: DeliveryEvaluationData) => {
    setDeliveryEvaluation(data);
    setCurrentStep((prev) => prev + 1);
  };

  const onSubmitStoreEvaluation = (data: StoreEvaluationData) => {
    setStoreEvalution(data);
    setCurrentStep((prev) => prev + 1);
  };

  const Screen = isWebMode ? OrderEvaluationWeb : OrderEvaluationMobile;

  return (
    <Screen
      order={order}
      isLoading={isLoading}
      isSubmiting={isSubmiting}
      currentStep={currentStep}
      onGoBack={goBack}
      onCancel={onCancel}
      onSubmitAppEvaluation={onSubmitAppEvaluation}
      onSubmitDeliveryEvaluation={onSubmitDeliveryEvaluation}
      onSubmitStoreEvaluation={onSubmitStoreEvaluation}
    />
  );
};

export default OrderEvaluation;
